import { ModelMap } from "hec/builder/model-map";
import { useHoverPopover } from "hec/hooks/useHoverPopover";
import useMediaQuery from "hec/hooks/useMediaQuery";
import type { ContextType, ReactNode } from "react";
import {
  Suspense,
  lazy,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-hot-toast";
import { useFetcher, useFetchers, useLocation, useMatches } from "react-router";

import {
  extractScapiErrorMessage,
  isScapiTimeout,
  upstreamTimeoutError,
} from "./scapi-error-message.server";
import { getTelehealthCheckoutKick } from "~/.server/sessions.server";
import {
  invariantResponseError,
  validationError,
} from "~/components/forms/validationErrorResponse.server";
import { resolveCartTelehealthBlock } from "~/hec/.server/cart-active-plan.server";
import { clearTelehealthKickIfPlanRemoved } from "~/hec/.server/telehealth-checkout-kick.server";
import { loadTreatments } from "~/hec/.server/treatments.server";
import {
  backfillVariantCategoryIds,
  resolveCategoryPaths,
} from "~/hec/analytics/category-path.server";
import { capPostAddProductRead } from "~/hec/basket/post-add-product-read.server";
import {
  externalProfileNeedsPassword,
  getAfidFromRequest,
  getTpaPurchaseLimitInfo,
} from "~/hec/helpers/tpa.server";
import { resolveRewardToApply } from "~/hec/loyalty/reward-apply.server";
import {
  buildCartUpdateForAction,
  cartUpdateProductIds,
} from "~/segment/cart-update.server";
import { ScapiBodyError } from "~/shop/shop.server";

import { fetchOneEntry } from "@builder.io/sdk-react";
import type {
  CustomRequestBody,
  ShopperBasketsTypes,
} from "@tsf/commerce-sdk-isomorphic";

import { BasketModel } from "~/commerce-api/models/basket";
import { SlasMintThrottledError } from "~/commerce-api/slas-mint-throttle";
import { isValidationError } from "~/components/forms/validationErrorResponse";
import { Link } from "~/components/link";
import { OverlayBoundary } from "~/components/overlay-boundary";
import { SkeletonAwait } from "~/components/suspense/skeleton";
import { Button } from "~/components/ui/button";
import { MinicartIcon } from "~/components/ui/icons/minicart-icon";
import { siteNameFromSiteId } from "~/config/environments";
import { useURL } from "~/contexts";
import { getBuilderConfig } from "~/hec/.server/config";
import { resolveCartLineDetail } from "~/hec/analytics/cart-line-detail";
import { buildSelectItemEvent } from "~/hec/analytics/event-layer/gtm-select-item-event";
import {
  type ViewCartEvent,
  buildViewCartEvent,
} from "~/hec/analytics/event-layer/gtm-view-cart";
import {
  basketLoaderFailureDetail,
  basketLoaderPartialMarker,
  basketLoaderStaleMarker,
  classifyBasketLoaderFailure,
  isPartialPart,
  isStaleBasketPayload,
  keyedBasketResult,
  reloadKeyedBasket,
  settleBasketEnrichment,
} from "~/hec/basket/basket-loader-soft.logic";
import type { BasketEnrichmentFailure } from "~/hec/basket/types";
import { useAuth } from "~/hec/context/auth-context";
import { useOverlaysReady } from "~/hec/hooks/useOverlaysReady";
import { rewardRejectedCopy } from "~/hec/loyalty/reward-apply.logic";
import { shouldRestampAddBackQrid } from "~/hec/telehealth/add-back-qrid";
import type { C_ProductInfo, ProductItem } from "~/lib/app.types";
import { SFL_UPDATE_FAILED } from "~/lib/constants";
import { isMinicartHidden } from "~/lib/handle-utils";
import { useBasketInvalidationRefresh } from "~/routes/resources/hooks/use-basket-invalidation-refresh";
import { getVariantImage } from "~/shop/utils/image";
import { useFormatPrice } from "~/shop/utils/price";

import { ANNEXCLOUD_REWARD_PAYMENT_ID } from "../cart/config/perks";
import { getMinicartSubtotal } from "../checkout/utils/totals";
import { isBasketMutationFetcher } from "./basket-mutation";
import {
  type BasketRowsApi,
  BasketRowsContext,
  type UnifiedRow,
} from "./components/basket-rows-context";
import {
  type SnapEntry,
  captureSnapEntry,
  deriveBasketRows,
  reconcileStaleSnaps,
} from "./components/basket-rows-derive";
import {
  BasketContext,
  BasketLineQuantityContext,
  type BasketLineQuantityReader,
  BasketReconcileContext,
  BasketSettledContext,
  useBasket,
  useBasketActions,
} from "./hooks/basket-hooks";
import { writePersistedCartCount } from "./hooks/persisted-cart-count";
import {
  useSaveForLater,
  useSaveForLaterActions,
} from "./hooks/save-for-later-hooks";
import { useFailSoftResourceState } from "./hooks/use-failsoft-resource";
import { useOnFetcherSettled } from "./hooks/use-fetcher-settled";
import { CART_ROUTE_ID } from "./save-for-later-provider";
import { BasketSchemaValidator, RESOURCE_URL } from "./schemas/basket";
import { SflActionResponseSchema } from "./schemas/save-for-later";
import { basketClientAction } from "./utils/basket-client-action";
import {
  isLoaderShaped,
  mutationStartedOnCart,
  selectBasketRaw,
} from "./utils/basket-data-select";
import { containedClientLoader } from "./utils/contained-client";
import {
  classifySaveLineItemSettle,
  saveLineItemNeedsRollback,
  shouldRollbackGuestSflWrite,
} from "./utils/sfl-settle";

// Submission-context captured at click time so settle observers (a) confirm a
// response belongs to OUR submission and (b) clean up state independent of
// whether the snap was dismissed mid-flight.
type SaveForLaterPending = {
  itemId: string;
  productId: string;
  isGuest: boolean;
  /** Guest only: the id was ALREADY in the localStorage list before this save,
   *  so `guestAdd` wrote nothing and a rollback must not remove it (COM-63). */
  alreadySaved: boolean;
};

// Basket `add` sources whose settle chains an SFL teardown from a hook that
// lives INSIDE the cart body (`useSflMoveToCart`, `RemovedItemCard`). While
// one is in flight the provider keeps the last rendered snapshot instead of
// swapping the body to a skeleton (P2-7b) — see `replayArmed` below.
const SFL_CHAINED_ADD_SOURCES = new Set(["sflMoveToCart", "addBack"]);
// Module-level empty sentinels stabilize the `?? []` fallback identity without
// per-render useMemo wrappers — same effect, zero hook overhead.
const EMPTY_PRODUCT_ITEMS: ProductItem[] = [];

// XD-189: snap cards ("Add Item Back") are transient confirmations. They
// auto-dismiss after this idle window (UX: 5s from the *last* trash / save),
// and the collapse animation runs for this long before the card unmounts.
const AUTO_DISMISS_MS = 5000;
const COLLAPSE_MS = 300;

export const BasketProvider = memo(({ children }: { children: ReactNode }) => {
  const url = useURL();
  // Shares the "basket" key with `useBasketActions` so action responses land
  // in the same fetcher state; action data has `basket` but not `products`,
  // so we trail every POST with a fresh loader call to refresh `products`.
  // Typed as the loader response — action responses share the `basket` field,
  // so reads still work; `products` falls back via the `in` guard below.
  const fetcher = useFetcher<typeof loader>({ key: "basket" });
  // Fail-soft mount hydrate (SFCC-11929): a bare `fetcher.load()` 4xx — e.g. a
  // Cloudflare managed-challenge 403 on this background XHR — is thrown to the
  // layout route's (missing) error boundary and bubbles to RootErrorBoundary,
  // blanking the whole storefront with the offline screen. Hydrate via a
  // guarded fetch that degrades to empty state instead. The shared "basket"
  // fetcher still drives every mutation/reload (account-drawer, cart, Minicart,
  // useBasketActions), and its data is preferred the moment any of those
  // populate it; `softBasket` only backs the pre-interaction mount window.
  // COM-63 — `scope` beacons a contained hydrate failure so a real outage is
  // visible in Workers Logs even though nothing crashes. Distinct from the
  // route's `basket.clientLoader` scope (router-driven loads) so the two paths
  // can be told apart in Workers Logs.
  const { data: softBasketRaw, settled: softSettled } =
    useFailSoftResourceState<NonNullable<typeof fetcher.data>>(
      url(RESOURCE_URL),
      undefined,
      "basket.hydrate",
    );
  // COM-840 — a stale loader payload (SCAPI timed out / 5xx'd) is not a
  // basket: treat it like a failed hydrate, never as a known-empty cart.
  const softBasket = isStaleBasketPayload(softBasketRaw)
    ? undefined
    : softBasketRaw;
  // Basket-invalidation refresh that SURVIVES navigation (SFCC-11878 follow-up,
  // generalised for SFCC-11869).
  //
  // Two things change the basket behind the client's back, and both leave the
  // shared keyed fetcher holding a stale snapshot:
  //   - a basket mutation. Those POST on component-scoped anonymous fetchers
  //     (`useBasketActions`), so the keyed "basket" loader only refreshes via a
  //     trailing `fetcher.load()`. Fired from the MUTATING component it was lost
  //     twice over — the owner unmounts before its effect runs, or the in-flight
  //     keyed load is canceled by the navigation ("View Cart" in the ATC
  //     flyout), leaving the cart page on a stale "empty" snapshot.
  //   - an auth flip. The /login action merges the guest basket into the
  //     registered one SERVER-side, but the mount hydrate above is a raw fetch
  //     that React Router's post-action revalidation never re-runs, so every
  //     consumer (header count, cart rows, minicart, the checkout hop) kept the
  //     guest snapshot until the next document load — HAR-verified.
  //
  // This provider is root-mounted and never unmounts, so it owns the refresh for
  // both: re-firing until loader-shaped data minted after the change lands, and
  // reporting `awaiting` so the stale pre-change fallback is suppressed (=>
  // loading state) rather than painted. Full mechanism: the hook's doc block.
  const { isLoggedIn } = useAuth();
  const fetchers = useFetchers();
  /* Reorder (COM-48) and the ET-30 telehealth plan add both mutate the same
     shared basket from their OWN resource routes, so matching only this
     route's URL would leave the minicart showing a pre-mutation snapshot.
     The registry lives in `basket-mutation.ts`; any future basket-mutating
     resource route has to be added there as well — the provider cannot infer
     it. */
  const basketMutationInFlight = fetchers.some(isBasketMutationFetcher);
  // COM-63 (P2-7b) — is the in-flight mutation an SFL-chained basket add
  // (move-to-cart / add-back)? That case arms the snapshot replay below on any
  // page; /cart also replays for mutations started there (COM-294).
  const sflChainedAddInFlight = fetchers.some(
    f =>
      isBasketMutationFetcher(f) &&
      SFL_CHAINED_ADD_SOURCES.has(String(f.formData?.get("source") ?? "")),
  );
  // ONE loader run per reload (COM-840): no `failSoftReload` preflight — the
  // route's contained `clientLoader` already keeps a WAF-challenge 403 off
  // RootErrorBoundary (SFCC-11929 / COM-63). See `reloadKeyedBasket`.
  // Ref-backed so `reconcileBasket` keeps ONE identity for the provider's
  // lifetime — it is published on `BasketReconcileContext` for a tile that
  // abandoned an add and must re-read the basket later (add-to-cart.tsx), and
  // a changing identity there would re-render every subscribed tile.
  const reloadBasketRef = useRef<() => void>(() => {});
  reloadBasketRef.current = () =>
    reloadKeyedBasket(href => fetcher.load(href), url(RESOURCE_URL));
  const reconcileBasket = useCallback(() => reloadBasketRef.current(), []);
  const awaitingFreshBasket = useBasketInvalidationRefresh({
    mutationInFlight: basketMutationInFlight,
    invalidatedBy: isLoggedIn,
    fetcherState: fetcher.state,
    fetcherData: fetcher.data,
    reload: reconcileBasket,
  });
  const lastProductsRef = useRef<C_ProductInfo[]>([]);
  const lastPromotionCalloutsRef = useRef<Record<string, string>>({});
  // ET-343: loader-only, like the two above — but unlike them `null` is a normal
  // value here (no block), so the carry-forward must key off the FIELD being
  // present rather than truthy. Keyed on truthiness it would latch the banner on
  // forever once shown; keyed on presence, an action response holds the last
  // loader verdict and the trailing re-load settles it.
  const lastTelehealthBlockRef =
    useRef<Awaited<ReturnType<typeof loader>>["telehealthBlock"]>(null);
  // COM-840: loader-only checkout-gate fields. Carried forward on PRESENCE (an
  // action response lacks them) so a stale / contained reload never resets
  // them to their defaults.
  const lastGateFieldsRef = useRef<{
    purchaseLimitInfo?: Awaited<ReturnType<typeof loader>>["purchaseLimitInfo"];
    needsPassword?: boolean;
    needsEmail?: boolean;
    afid?: string | null;
  }>({});
  // Hoisted above `data` so the basket-source selection can see whether an
  // optimistic remove/SFL snap is in flight (see `selectBasketRaw`).
  const [snaps, setSnaps] = useState<Map<string, SnapEntry>>(new Map());
  // LATCHED, not read live off `snaps.size`. The two clocks don't line up: the
  // shared auto-dismiss retires every snap at AUTO_DISMISS_MS + COLLAPSE_MS
  // (~5.3s), while `awaitingFreshBasket` can stay true for the hook's full
  // max-await (8s) — which is exactly what happens when the keyed reload is slow
  // or keeps getting skipped by the fail-soft preflight (the SFCC-11929
  // WAF-challenge case). Read live, the overlay claim would drop at ~5.3s with
  // the await still open, blanking `softBasket` and painting CartSkeleton for
  // the remaining window: cart → skeleton → cart, which reads worse than the
  // pre-fix "skeleton throughout". So once a snap has been captured we hold the
  // claim until the await it covers ends (`releaseSnapLatch` below).
  const snapLatchRef = useRef(false);
  const latchSnapCapture = useCallback(() => {
    snapLatchRef.current = true;
  }, []);
  // Released from three places, all of which re-render on their own (state in
  // this component or in `useBasketInvalidationRefresh`), so a ref write is
  // enough — nothing needs to schedule a render just to observe it:
  //   1. the await ENDING — its true→false down-edge (below); outside an await
  //      window `hasOptimisticSnap` doesn't gate anything, so releasing an
  //      effect-tick late is invisible;
  //   2. SPA nav and 3. auth flip — those dismiss because the basket is being
  //      replaced wholesale, so the overlay must stop vouching for `softBasket`
  //      and let it fall through to the loading state. Without the explicit
  //      release the latch would outlive the dismissal and re-introduce the
  //      stale-pre-merge-basket bug those two effects exist to prevent.
  const releaseSnapLatch = useCallback(() => {
    snapLatchRef.current = false;
  }, []);
  const hasOptimisticSnap = snaps.size > 0 || snapLatchRef.current;
  // DOWN-EDGE only (prev-value ref), and `snaps` deliberately NOT in the deps.
  // A level check (`if (!awaiting) release()`) that also re-runs on snap changes
  // releases the latch one effect tick after it is set, before the await it
  // covers has armed — so the latch never holds and `hasOptimisticSnap` stays
  // effectively `snaps.size > 0`:
  //   `useBasketInvalidationRefresh` is called ABOVE this effect (L173), so its
  //   effects flush first. On a trash click `setSnaps`, `latchSnapCapture()` and
  //   the fetcher submit batch into ONE render where `awaitingFreshBasket` is
  //   still false. Effects then flush in declaration order: the hook's
  //   `setAwaiting(true)` doesn't interrupt the flush, so this effect runs next
  //   against *this* render's closure — `false` — and clears the latch.
  // A capture can't fail to arm an await (both capture sites fire the remove /
  // SFL POST, which sets `mutationInFlight`, so `setAwaiting(true)` always
  // follows), and if one ever did leak, the next await's down-edge clears it
  // before it can vouch for a basket that isn't its own.
  const wasAwaitingFreshBasketRef = useRef(awaitingFreshBasket);
  useEffect(() => {
    const wasAwaiting = wasAwaitingFreshBasketRef.current;
    wasAwaitingFreshBasketRef.current = awaitingFreshBasket;
    if (wasAwaiting && !awaitingFreshBasket) releaseSnapLatch();
  }, [awaitingFreshBasket, releaseSnapLatch]);
  type BasketData = NonNullable<ContextType<typeof BasketContext>>;
  type KeyedData = NonNullable<typeof fetcher.data>;
  // COM-63 — last LOADER-shaped keyed payload. A contained clientLoader
  // failure resolves `fetcher.data` to `null`; falling back to `softBasket`
  // then (the one-shot MOUNT fetch, which predates every mutation) would drop
  // the header count to the page-load value. Prefer this instead.
  const lastGoodKeyedRef = useRef<KeyedData | undefined>(undefined);
  if (fetcher.data && isLoaderShaped(fetcher.data)) {
    lastGoodKeyedRef.current = fetcher.data;
  }
  // Last snapshot this provider actually handed to consumers — replayed ONLY
  // while `awaitingFreshBasket` was armed by an SFL-chained add (P2-7b) or, on
  // /cart, by a mutation started there (COM-294).
  const lastDataRef = useRef<BasketData | undefined>(undefined);
  // COM-63 (P2-7b) — arm the replay when an SFL-chained add is observed; keep
  // it armed through the refresh window; disarm once the refresh settles. A
  // plain ATC / qty edit / trash OFF /cart never arms it, so those keep the
  // SFCC-11878/11869 suppression: the pre-mutation basket is NOT painted (no
  // stale header count, no stale GA4 view_cart).
  const replayArmedRef = useRef(false);
  if (sflChainedAddInFlight) replayArmedRef.current = true;
  else if (!awaitingFreshBasket) replayArmedRef.current = false;
  // COM-294: a mutation STARTED on /cart keeps the already-painted basket there
  // instead of the full-page skeleton, and only while the shopper stays on /cart.
  const onCart = useMatches().some(m => m.id === CART_ROUTE_ID);
  const startedOnCartRef = useRef<boolean | null>(null);
  startedOnCartRef.current = mutationStartedOnCart(startedOnCartRef.current, {
    inFlight: basketMutationInFlight,
    awaiting: awaitingFreshBasket,
    onCart,
  });
  const replayArmed =
    replayArmedRef.current || (onCart && startedOnCartRef.current === true);
  // An auth flip also arms `awaitingFreshBasket` (SFCC-11869). After a login
  // the last snapshot is the GUEST basket — never replay across the flip.
  const lastAuthRef = useRef(isLoggedIn);
  if (lastAuthRef.current !== isLoggedIn) {
    lastAuthRef.current = isLoggedIn;
    lastDataRef.current = undefined;
    replayArmedRef.current = false;
  }
  const data = useMemo(() => {
    // Precedence lives in `selectBasketRaw` (pure, unit-tested):
    //  1. keyed fetcher payload (loader OR action response) — freshest truth;
    //  2. awaiting a refresh AND an SFL move (or a mutation started on /cart,
    //     COM-294) armed the replay → the snapshot
    //     already rendered, so the cart body — and the `useSflMoveToCart`
    //     instance owning the pending move + its submit fetcher / finishMove
    //     timer — survive the refresh window (P2-7b: otherwise the body swapped
    //     to <CartSkeleton>, the hook unmounted, and the SFL cleanup leg never
    //     fired → item in cart AND Saved for Later). A `null` keyed payload
    //     (contained reload failure) counts like `undefined` here;
    //  3. `null` → last good KEYED payload, then `softBasket` — even while
    //     awaiting (the refresh hook bounds the wait), never a whole-cart
    //     skeleton while a stale null lingers on the fetcher;
    //  4. awaiting an ordinary post-mutation refresh → suppress the
    //     pre-mutation `softBasket` (=> loading state; SFCC-11878/11869)
    //     — unless an optimistic remove/SFL snap is overlaying it;
    //  5. otherwise the mount-time `softBasket`.
    const selected = selectBasketRaw<KeyedData, BasketData>({
      fetcherData: fetcher.data as KeyedData | null | undefined,
      softBasket,
      lastGoodKeyed: lastGoodKeyedRef.current,
      awaiting: awaitingFreshBasket,
      replayArmed,
      replaySnapshot: lastDataRef.current,
      hasOptimisticSnap,
    });
    if (selected.kind === "replay") return selected.snapshot;
    if (selected.kind === "none") return undefined;
    const raw = selected.raw;
    const basket = "basket" in raw ? raw.basket : undefined;
    if (!basket) return undefined;
    // Loader responses include `products`; action responses don't — keep the
    // last loader-fetched products array so consumers (cart drawer, etc.) don't
    // see `undefined` between the action returning and the trailing re-load.
    // COM-902: a PARTIAL loader payload (an enrichment read degraded server-
    // side) gets the same carry-forward for each part it marks — its empty
    // fallback means "unknown", not "none". Lines the last good products don't
    // cover stay unresolved, which the ET-393 telehealth gate reads as closed.
    const productsDegraded = isPartialPart(raw, "products");
    const products =
      "products" in raw && raw.products && !productsDegraded
        ? raw.products
        : lastProductsRef.current;
    lastProductsRef.current = products;
    // Same treatment for the promotion callout map (SFCC-11931): loader-only
    // payload, carried across action responses until the trailing re-load
    // refreshes it.
    const promotionCallouts =
      "promotionCallouts" in raw &&
      raw.promotionCallouts &&
      !isPartialPart(raw, "promotionCallouts")
        ? raw.promotionCallouts
        : lastPromotionCalloutsRef.current;
    lastPromotionCalloutsRef.current = promotionCallouts;
    // SFCC-12141: analytics-only, action responses don't carry it — `?? null`
    // (not a "last known" ref like products/promotionCallouts above) so a
    // post-mutation action response doesn't replay a stale view_cart push.
    const gtmViewCart = "gtmViewCart" in raw ? (raw.gtmViewCart ?? null) : null;
    // COM-216: same treatment — loader-only, and the mini-cart needs it on the
    // cold guest load that the fail-soft hydrate (not the keyed fetcher) serves.
    const emptyMinicartContent =
      "emptyMinicartContent" in raw ? (raw.emptyMinicartContent ?? null) : null;
    // ET-343 — see lastTelehealthBlockRef: presence-keyed, not truthiness-keyed.
    // COM-902: the server can't classify a line without its product, so a
    // products partial's `null` is not a verdict — keep the last one.
    const telehealthBlock =
      "telehealthBlock" in raw && !productsDegraded
        ? (raw.telehealthBlock ?? null)
        : lastTelehealthBlockRef.current;
    lastTelehealthBlockRef.current = telehealthBlock;
    const last = lastGateFieldsRef.current;
    const checkoutGate = {
      purchaseLimitInfo:
        "purchaseLimitInfo" in raw && !isPartialPart(raw, "purchaseLimitInfo")
          ? raw.purchaseLimitInfo
          : last.purchaseLimitInfo,
      needsPassword:
        "needsPassword" in raw ? raw.needsPassword : last.needsPassword,
      needsEmail: "needsEmail" in raw ? raw.needsEmail : last.needsEmail,
      afid: "afid" in raw ? raw.afid : last.afid,
    };
    lastGateFieldsRef.current = checkoutGate;
    const next: BasketData = {
      ...checkoutGate,
      basket: new BasketModel(basket),
      products,
      promotionCallouts,
      gtmViewCart,
      emptyMinicartContent,
      telehealthBlock,
    };
    lastDataRef.current = next;
    return next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetcher.data,
    softBasket,
    awaitingFreshBasket,
    hasOptimisticSnap,
    replayArmed,
  ]);

  // Local ProductItem (from ~/lib/app.types) is what BasketLineItem.Root and
  // the rest of this app consume. BasketModel.productItems is the SDK type
  // (structurally compatible but not assignable through strict predicates),
  // so cast at this single boundary.
  const productItems =
    (data?.basket?.productItems as ProductItem[] | undefined) ??
    EMPTY_PRODUCT_ITEMS;

  // Ref-backed line-quantity reader for the tile add's uncertain-state
  // protocol (`use-basket-add-reconcile.ts`), published on its own stable
  // context so a polling tile reads the CURRENT basket without subscribing to
  // it. `undefined` = no basket loaded yet (or suppressed while awaiting a
  // fresh one) — distinct from `0` = loaded and the product is not in it.
  const lineQuantityRef = useRef<BasketLineQuantityReader>(() => undefined);
  lineQuantityRef.current = productId => {
    if (!data?.basket) return undefined;
    let quantity = 0;
    for (const item of productItems) {
      if (item.productId === productId) quantity += item.quantity ?? 0;
    }
    return quantity;
  };
  const readLineQuantity = useCallback<BasketLineQuantityReader>(
    productId => lineQuantityRef.current(productId),
    [],
  );

  // SFCC-11599 — persist the resolved line-item count to the `hec_cart_count`
  // cookie so the /cart loader can shape the loading skeleton (empty / 1 / N
  // rows) on the FIRST server paint of the next COLD load, before the client
  // basket fetch returns. Guard on `data?.basket` so cold-load mounts (where
  // productItems is the EMPTY fallback) don't clobber the cookie with a
  // premature 0.
  useEffect(() => {
    if (!data?.basket) return;
    writePersistedCartCount(productItems.length);
  }, [data, productItems.length]);

  const [restoredPositions, setRestoredPositions] = useState<
    Map<string, number>
  >(new Map());
  const [restoringReplacements, setRestoringReplacements] = useState<
    Map<string, string>
  >(new Map());

  // XD-189 animation state. `leaving` = snap itemIds mid collapse-animation
  // (card stays mounted through the COLLAPSE_MS transition, then is removed).
  // `savingForLater` = line-item itemIds playing the SFL lift/slide/fade exit.
  const [leaving, setLeaving] = useState<Set<string>>(new Set());
  const [savingForLater, setSavingForLater] = useState<Set<string>>(new Set());

  // Honour OS reduced-motion: skip the collapse/exit animations and remove
  // snaps instantly. Only ever read inside client callbacks (never rendered),
  // so the SSR-false → client-true settle is safe (no hydration mismatch).
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  // Live mirror of `snaps` so the auto-dismiss timer + nav handler read the
  // current set without stale closures.
  const snapsRef = useRef(snaps);
  useEffect(() => {
    snapsRef.current = snaps;
  }, [snaps]);
  // Single shared auto-dismiss timer (reset on each new trash / save) + the set
  // of in-flight collapse timeouts (cleared on unmount).
  const autoDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const collapseTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(
    new Set(),
  );

  // Remove snaps (+ their errors / leaving flags) from state — the terminal
  // step of every dismissal path.
  const finalizeDismiss = useCallback((ids: readonly string[]) => {
    if (ids.length === 0) return;
    setSnaps(prev => {
      let next: Map<string, SnapEntry> | null = null;
      for (const id of ids) {
        if (prev.has(id)) (next ??= new Map(prev)).delete(id);
      }
      return next ?? prev;
    });
    setSnapErrors(prev => {
      let next: Map<string, string> | null = null;
      for (const id of ids) {
        if (prev.has(id)) (next ??= new Map(prev)).delete(id);
      }
      return next ?? prev;
    });
    setLeaving(prev => {
      let next: Set<string> | null = null;
      for (const id of ids) {
        if (prev.has(id)) (next ??= new Set(prev)).delete(id);
      }
      return next ?? prev;
    });
  }, []);

  // Animate a batch of snap cards out (collapse), then finalize. Passing the
  // whole set at once makes them collapse simultaneously (UX: no staggered
  // shifting). Reduced-motion → finalize immediately.
  const animateDismiss = useCallback(
    (ids: readonly string[]) => {
      const targets = ids.filter(id => snapsRef.current.has(id));
      if (targets.length === 0) return;
      if (reduceMotion) {
        finalizeDismiss(targets);
        return;
      }
      setLeaving(prev => {
        const next = new Set(prev);
        for (const id of targets) next.add(id);
        return next;
      });
      const timer = setTimeout(() => {
        collapseTimersRef.current.delete(timer);
        finalizeDismiss(targets);
      }, COLLAPSE_MS);
      collapseTimersRef.current.add(timer);
    },
    [reduceMotion, finalizeDismiss],
  );

  const clearAutoDismiss = useCallback(() => {
    if (autoDismissTimerRef.current !== null) {
      clearTimeout(autoDismissTimerRef.current);
      autoDismissTimerRef.current = null;
    }
  }, []);

  // (Re)start the 5s timer. Called on every trash / save so the countdown runs
  // from the *last* action; on expiry, every open snap card dismisses together.
  const scheduleAutoDismiss = useCallback(() => {
    clearAutoDismiss();
    autoDismissTimerRef.current = setTimeout(() => {
      autoDismissTimerRef.current = null;
      animateDismiss([...snapsRef.current.keys()]);
    }, AUTO_DISMISS_MS);
  }, [clearAutoDismiss, animateDismiss]);

  // ✕ button dismissal entry point (animated single card).
  const dismissSnap = useCallback(
    (itemId: string) => {
      animateDismiss([itemId]);
    },
    [animateDismiss],
  );

  // Stop the timer once no cards remain so it can't fire spuriously; cancel
  // every pending timer on unmount.
  useEffect(() => {
    if (snaps.size === 0) clearAutoDismiss();
  }, [snaps, clearAutoDismiss]);
  useEffect(() => {
    // Stable Set (never reassigned) — capture once for the cleanup closure.
    const collapseTimers = collapseTimersRef.current;
    return () => {
      clearAutoDismiss();
      for (const timer of collapseTimers) clearTimeout(timer);
      collapseTimers.clear();
    };
  }, [clearAutoDismiss]);

  // Keep `savingForLater` to only items still mid-flight — pruned when the item
  // leaves the basket (SFL committed → card takes over) or its snap is dropped
  // (rollback → tile returns to normal). Prevents a stuck faded-out tile.
  useEffect(() => {
    setSavingForLater(prev => {
      if (prev.size === 0) return prev;
      let next: Set<string> | null = null;
      for (const id of prev) {
        const stillPending =
          snaps.has(id) && productItems.some(i => i.itemId === id);
        if (!stillPending) (next ??= new Set(prev)).delete(id);
      }
      return next ?? prev;
    });
  }, [productItems, snaps]);

  // Keep restored-position hints only while their replacement line is still in
  // the basket. If the item is removed later, drop the stale row-order hint.
  useEffect(() => {
    setRestoredPositions(prev => {
      if (prev.size === 0) return prev;
      const liveItemIds = new Set(
        productItems.map(item => item.itemId).filter(Boolean),
      );
      let next: Map<string, number> | null = null;
      for (const itemId of prev.keys()) {
        if (!liveItemIds.has(itemId)) (next ??= new Map(prev)).delete(itemId);
      }
      return next ?? prev;
    });
  }, [productItems]);

  // Reconcile: drop any snap whose product is back in the basket by an
  // external path (SavedForLaterSection "Move to cart", PDP ATC, favorites
  // move, cross-tab). Symmetric for "removed" and "savedForLater" snaps.
  // Pure predicate lives in `basket-rows-derive.ts` (covered by unit tests).
  // Also clears any orphan `snapErrors` entries for the dropped snaps.
  useEffect(() => {
    let dropped: Set<string> | null = null;
    setSnaps(prev => {
      const toDrop = reconcileStaleSnaps(productItems, prev);
      if (toDrop.size === 0) return prev;
      dropped = toDrop;
      const next = new Map(prev);
      for (const itemId of toDrop) next.delete(itemId);
      return next;
    });
    if (dropped && (dropped as Set<string>).size > 0) {
      setSnapErrors(prev => {
        let changed = false;
        const next = new Map(prev);
        for (const itemId of dropped as Set<string>) {
          if (next.delete(itemId)) changed = true;
        }
        return changed ? next : prev;
      });
    }
  }, [productItems]);

  // SFCC-11094 follow-up (F3) + XD-189: snap cards ("Add Item Back") are a
  // transient affordance — dismiss them on navigation so they don't linger in
  // the cart / mini-cart. A full-page nav remounts this provider with empty
  // snaps; this handles SPA route changes (the mini-cart persists in the header
  // across them). XD-189 unified the lifecycle, so BOTH removed and
  // saved-for-later cards now dismiss on nav (vs the 5s timer, whichever fires
  // first), collapsing on the way out.
  const { pathname } = useLocation();
  const lastPathRef = useRef(pathname);
  useEffect(() => {
    if (pathname === lastPathRef.current) return;
    lastPathRef.current = pathname;
    releaseSnapLatch();
    animateDismiss([...snapsRef.current.keys()]);
  }, [pathname, animateDismiss, releaseSnapLatch]);

  // Auth flip (guest⇄registered) merges the basket SERVER-side, so any in-flight
  // "Add Item Back" / SFL snap describes the PRE-merge basket and is no longer
  // meaningful — dismiss it, mirroring the nav-dismiss above. Without this a
  // leftover snap would keep `hasOptimisticSnap` true and let a stale
  // pre-merge `softBasket` mask the merged basket instead of showing loading
  // (SFCC-11878), in the window before the keyed reload lands. The account
  // drawer revalidates in place (no pathname change), so the nav-dismiss can't
  // cover this.
  //
  // `finalizeDismiss`, NOT `animateDismiss`: the collapse animation would keep
  // the snap (and therefore `hasOptimisticSnap`) alive for COLLAPSE_MS, and that
  // window is precisely the one this effect is supposed to close — the stale
  // pre-merge `softBasket` would still paint through it. There's also nothing
  // worth animating out: the basket is being replaced wholesale, so the card
  // isn't collapsing to reveal its old slot, it's describing a basket that no
  // longer exists.
  const lastLoggedInRef = useRef(isLoggedIn);
  useEffect(() => {
    if (isLoggedIn === lastLoggedInRef.current) return;
    lastLoggedInRef.current = isLoggedIn;
    releaseSnapLatch();
    finalizeDismiss([...snapsRef.current.keys()]);
  }, [isLoggedIn, finalizeDismiss, releaseSnapLatch]);

  // Derive rows at render-time. No sync useEffect → no first-paint flicker.
  // See `basket-rows-derive.ts` for the pure logic (covered by unit tests).
  const rows = useMemo<UnifiedRow[]>(
    () =>
      deriveBasketRows(
        productItems,
        snaps,
        restoredPositions,
        restoringReplacements,
      ),
    [productItems, snaps, restoredPositions, restoringReplacements],
  );

  // Dedicated `useBasketActions` instance so the remove-settle observer below
  // doesn't fire on unrelated submits (per `feedback_basket_fetcher_split.md`).
  const removeActions = useBasketActions();
  // Save-for-Later is available only inside `SaveForLaterProvider` (mounted
  // outside `BasketProvider` in `layout.tsx`). Both hook calls are no-op-safe
  // when SaveForLaterProvider is absent (e.g. the checkout layout):
  // `useSaveForLater()` returns undefined; `useSaveForLaterActions()` only
  // wires fetchers and tolerates that.
  const sfl = useSaveForLater();
  const sflActions = useSaveForLaterActions();

  // Per-fetcher in-flight refs — separate so the trash flow and the SFL save
  // flow don't cross-block (different fetchers, independent submissions).
  // Same-row dedup is handled by the `snaps.has(itemId)` guard below.
  const removeInFlightRef = useRef<string | null>(null);
  const saveForLaterPendingRef = useRef<SaveForLaterPending | null>(null);

  // Card-driven re-add (`<RemovedItemCard>` owns its own `useBasketActions`
  // and writes here on validation error). Card's settle observer also writes
  // here when the basket-add leg of a SFL move-to-cart fails.
  const [snapErrors, setSnapErrors] = useState<Map<string, string>>(new Map());

  const setSnapError = useCallback((itemId: string, msg: string) => {
    setSnapErrors(prev => {
      const next = new Map(prev);
      next.set(itemId, msg);
      return next;
    });
  }, []);

  const dropSnap = useCallback((itemId: string) => {
    setSnaps(prev => {
      if (!prev.has(itemId)) return prev;
      const next = new Map(prev);
      next.delete(itemId);
      return next;
    });
    setSnapErrors(prev => {
      if (!prev.has(itemId)) return prev;
      const next = new Map(prev);
      next.delete(itemId);
      return next;
    });
    // Clear any stale collapse flag so a dropped snap can't linger in `leaving`.
    setLeaving(prev => {
      if (!prev.has(itemId)) return prev;
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  }, []);

  // Keep the snap card mounted until the replacement line from "Add Item Back"
  // is present in the basket data. `deriveBasketRows` hides the card and shows
  // the restored row in that same render, avoiding a one-tick disappear/reappear
  // shuffle while the keyed basket loader catches up.
  useEffect(() => {
    if (restoringReplacements.size === 0) return;
    const liveItemIds = new Set(
      productItems.map(item => item.itemId).filter(Boolean),
    );
    const restoredSnapIds = [...restoringReplacements.entries()]
      .filter(([, replacementItemId]) => liveItemIds.has(replacementItemId))
      .map(([snapItemId]) => snapItemId);
    if (restoredSnapIds.length === 0) return;

    setRestoringReplacements(prev => {
      let next: Map<string, string> | null = null;
      for (const snapItemId of restoredSnapIds) {
        if (prev.has(snapItemId)) {
          (next ??= new Map(prev)).delete(snapItemId);
        }
      }
      return next ?? prev;
    });
    for (const snapItemId of restoredSnapIds) dropSnap(snapItemId);
  }, [dropSnap, productItems, restoringReplacements]);

  const restoreSnap = useCallback(
    (itemId: string, replacementItemId?: string) => {
      const snap = snapsRef.current.get(itemId);
      if (snap && replacementItemId) {
        setRestoredPositions(prev => {
          const next = new Map(prev);
          next.set(replacementItemId, snap.originalIndex);
          return next;
        });
        setRestoringReplacements(prev => {
          const next = new Map(prev);
          next.set(itemId, replacementItemId);
          return next;
        });
        return;
      }
      dropSnap(itemId);
    },
    [dropSnap],
  );

  const removeLineItem = useCallback(
    (lineItem: ProductItem, providedOriginalIndex?: number) => {
      if (!lineItem.itemId) return;
      const itemId = lineItem.itemId;
      // Single-flight trash. Different fetcher from SFL save, so no
      // cross-block — concurrent SFL on a different row is fine.
      if (removeInFlightRef.current !== null) return;
      // Dedup: row already captured (rapid double-click on same row).
      if (snaps.has(itemId)) return;
      // Cross-tab edit cleared it.
      if (!productItems.some(i => i.itemId === itemId)) return;

      // Snap requires productId — bundle children / orphans skip snap but
      // the remove still goes through. Without productId, the re-add `add`
      // wire payload can't be reconstructed, so a snap card would be useless.
      if (lineItem.productId) {
        const productItemsIndex = productItems.findIndex(
          i => i.itemId === itemId,
        );
        const originalIndex = providedOriginalIndex ?? productItemsIndex;
        setSnaps(prev => {
          if (prev.has(itemId)) return prev;
          const next = new Map(prev);
          next.set(
            itemId,
            captureSnapEntry({
              lineItem,
              source: "removed",
              originalIndex,
              fallbackIndex: prev.size,
              // `lastProductsRef`, deliberately: the product list as of THIS
              // click. Once SCAPI confirms the remove the telehealth product
              // drops out of `products` and the removed row could no longer be
              // classified telehealth, which would push its "Add Item Back" card
              // out of the telehealth-first group (ET-204).
              products: lastProductsRef.current,
            }),
          );
          return next;
        });
        latchSnapCapture();
        // XD-189: a card just appeared — (re)start the shared 5s auto-dismiss.
        scheduleAutoDismiss();
      }

      removeInFlightRef.current = itemId;
      removeActions.remove(itemId, { line: lineItem });
    },
    [
      removeActions.remove,
      productItems,
      snaps,
      scheduleAutoDismiss,
      latchSnapCapture,
    ],
  );

  // Remove-settle observer. Handles trash AND guest SFL forward (which
  // submits the basket-remove leg through this fetcher). Auth SFL forward
  // uses the atomic `saveLineItem` compound on a different fetcher — see the
  // addFetcher observer below. Trash correlates via `removeInFlightRef`;
  // guest SFL via `saveForLaterPendingRef.isGuest`. On validation error,
  // drop the snap so trash/save buttons re-enable; for guest SFL failure,
  // also roll back the optimistic localStorage write.
  useOnFetcherSettled(removeActions.fetcher, data => {
    const trashItemId = removeInFlightRef.current;
    const sflGuestPending =
      saveForLaterPendingRef.current?.isGuest === true
        ? saveForLaterPendingRef.current
        : null;
    removeInFlightRef.current = null;
    if (sflGuestPending) saveForLaterPendingRef.current = null;
    const targetItemId = trashItemId ?? sflGuestPending?.itemId;
    if (!targetItemId) return;
    if (!isValidationError(data)) return;
    if (sflGuestPending?.productId) {
      // Guest SFL save failed at the basket-remove leg (server-caught SCAPI
      // error, OR a network-level failure the clientAction contained into an
      // empty validationError — COM-63 P1-3). Roll back the optimistic
      // localStorage write — but ONLY if this save wrote it: `guestAdd` on an
      // already-saved id is a no-op success, and removing that would delete a
      // pre-existing saved entry. Tell the shopper; the flyout skips this
      // submission (`sflMove` marker) so this is the only toast.
      if (shouldRollbackGuestSflWrite(sflGuestPending)) {
        sfl?.guestRemove(sflGuestPending.productId);
      }
      toast.error(SFL_UPDATE_FAILED);
    }
    // A pure trash failure is surfaced by the AddedToBasketFlyout watcher
    // (server message, or BASKET_REMOVE_FAILED when the payload is empty) —
    // not here, to avoid a double toast. Drop the snap so the row is live.
    dropSnap(targetItemId);
  });

  // SFL forward path. Auth uses the server-side atomic `saveLineItem`
  // compound (SFL add + basket remove in one round-trip); guest writes SFL
  // via localStorage synchronously and submits a basket remove via
  // `removeActions` (the remove-settle observer above rolls back the
  // localStorage write on validation error).
  const saveForLater = useCallback(
    (lineItem: ProductItem, providedOriginalIndex?: number) => {
      if (!lineItem.itemId || !lineItem.productId) return;
      const itemId = lineItem.itemId;
      const productId = lineItem.productId;
      if (saveForLaterPendingRef.current !== null) return;
      if (snaps.has(itemId)) return;
      if (!productItems.some(i => i.itemId === itemId)) return;

      const productItemsIndex = productItems.findIndex(
        i => i.itemId === itemId,
      );
      const originalIndex = providedOriginalIndex ?? productItemsIndex;
      setSnaps(prev => {
        if (prev.has(itemId)) return prev;
        const next = new Map(prev);
        // Same capture as the remove path — telehealth-ness snapshotted from the
        // pre-settle product list so the SFL card keeps its telehealth-first slot.
        next.set(
          itemId,
          captureSnapEntry({
            lineItem,
            source: "savedForLater",
            originalIndex,
            fallbackIndex: prev.size,
            products: lastProductsRef.current,
          }),
        );
        return next;
      });
      latchSnapCapture();
      // XD-189: animate the line-item tile out (lift → slide down → fade). The
      // SFL card is revealed underneath once the server settles (derive); this
      // flag is pruned by the effect above on settle / rollback.
      setSavingForLater(prev => {
        if (prev.has(itemId)) return prev;
        const next = new Set(prev);
        next.add(itemId);
        return next;
      });
      scheduleAutoDismiss();

      // Auth from the canonical AuthProvider (`isLoggedIn` above), not the SFL
      // fetch — COM-63 gated that fetch to /cart, so off-cart (the minicart)
      // its payload would read every shopper as a guest.
      const isGuest = !isLoggedIn;
      // Guest: captured BEFORE `guestAdd` so a failed remove leg only rolls
      // back a write this save actually made (COM-63 review, WARN-4).
      const alreadySaved = isGuest && (sfl?.isInList(productId) ?? false);
      saveForLaterPendingRef.current = {
        itemId,
        productId,
        isGuest,
        alreadySaved,
      };
      if (isGuest) {
        // COM-63 (P2-9b) — persist to SFL localStorage BEFORE removing from
        // the basket, and ABORT the move when the write fails (quota / private
        // mode / blocked): never remove from the cart with nothing saved
        // (silent data loss). Revert the optimistic tile + surface the failure.
        // `guestAdd` beacons `guest-save-for-later.write` before returning.
        if (sfl?.guestAdd(productId) === false) {
          saveForLaterPendingRef.current = null;
          dropSnap(itemId);
          toast.error(SFL_UPDATE_FAILED);
          return;
        }
        // Order is deliberate (P1-3): write first, then remove — a failed
        // remove leg rolls the write back in the settle observer above, whereas
        // remove-then-write would lose the item when the write fails.
        removeActions.remove(itemId, { sflMove: true, line: lineItem });
      } else {
        sflActions.saveLineItem(productId, itemId);
      }
    },
    [
      productItems,
      snaps,
      sfl,
      sflActions,
      removeActions.remove,
      scheduleAutoDismiss,
      latchSnapCapture,
      isLoggedIn,
      dropSnap,
    ],
  );

  // SFL save (auth) settle observer. Server compound returns `partial:true`
  // when one of the two legs failed. On partial / failure: drop the snap so
  // the row is mutable again (otherwise `snaps.has(id)` would block trash +
  // save retries indefinitely). On full success: snap stays — derive reveals
  // it once productItems updates server-side. Always clear the pending ref
  // on any settle so the save flow doesn't dead-lock.
  useOnFetcherSettled(sflActions.addFetcher, data => {
    // Auth saveLineItem also removed a basket line server-side. Refetch
    // basket so productItems reflects the new state — without this, the
    // reconcile effect above never fires and the snap card stays stale.
    // One loader run, contained by the route's `clientLoader` (COM-840).
    reloadBasketRef.current();
    const pending = saveForLaterPendingRef.current;
    if (!pending || pending.isGuest) return;
    saveForLaterPendingRef.current = null;
    // Classification lives in `sfl-settle.ts` (pure, unit-tested):
    //  - failed  = validationError / contained `sfl-error` / shape drift;
    //  - partial = SFL add ok but the basket-remove leg failed (item in both);
    //  - duplicate = the product was ALREADY saved server-side and the basket
    //    line was still removed — the move DID complete; with the SFL list
    //    only loading on /cart the minicart can't know, so this is common and
    //    must NOT read as a failure (COM-63 review, BLOCK-1);
    //  - success.
    // On failed/partial: drop the snap so the row is mutable again and tell
    // the shopper (P1-1 B: this used to be silent — the row just came back,
    // and stayed dimmed). On success/duplicate: the snap stays; derive reveals
    // it once productItems updates server-side.
    const outcome = classifySaveLineItemSettle(data);
    if (saveLineItemNeedsRollback(outcome)) {
      dropSnap(pending.itemId);
      toast.error(SFL_UPDATE_FAILED);
    }
  });

  // After a basket line is added via the keyed `sfl-move-cleanup` SFL remove
  // (chained by `RemovedItemCard` for auth-SFL reverse, or by
  // `useSflMoveToCart` for the cart-page SFL list), refresh the basket loader
  // so productItems updates and the reconcile effect can drop any stale snap.
  useOnFetcherSettled(sflActions.cleanupFetcher, () => {
    // One loader run, contained by the route's `clientLoader` (COM-840).
    reloadBasketRef.current();
  });

  const rowsApi = useMemo<BasketRowsApi>(
    () => ({
      rows,
      removeLineItem,
      saveForLater,
      dropSnap,
      restoreSnap,
      dismissSnap,
      setSnapError,
      snapErrors,
      leaving,
      savingForLater,
    }),
    [
      rows,
      removeLineItem,
      saveForLater,
      dropSnap,
      restoreSnap,
      dismissSnap,
      setSnapError,
      snapErrors,
      leaving,
      savingForLater,
    ],
  );

  return (
    <BasketReconcileContext.Provider value={reconcileBasket}>
      <BasketLineQuantityContext.Provider value={readLineQuantity}>
        <BasketContext.Provider value={data}>
          <BasketSettledContext.Provider
            // A withheld stale snapshot is not "settled", or the spender reads
            // it as a known empty cart.
            value={
              fetcher.data !== undefined ||
              (softSettled && !awaitingFreshBasket)
            }
          >
            <BasketRowsContext.Provider value={rowsApi}>
              {children}
            </BasketRowsContext.Provider>
          </BasketSettledContext.Provider>
        </BasketContext.Provider>
      </BasketLineQuantityContext.Provider>
    </BasketReconcileContext.Provider>
  );
});
BasketProvider.displayName = "BasketProvider";

// SFCC-11918 — during a SLAS mint cooldown (`Auth.ready()` throws
// `SlasMintThrottledError` for ~8s org-wide after an oauth2/token 429) every
// SCAPI read in here rejects, and this loader 500s. The client already
// fail-softs (`useFailSoftResource`, SFCC-11929) so the page survives, but the
// 500 is logged per request and accounted for a large share of the 8,830
// `HandleError: SlasMintThrottledError` events over 3 days on fsa-production.
// Degrade server-side instead: render the minicart as empty for the cooldown
// window. Every field below is the same shape the happy path returns with an
// absent basket, so no consumer sees a widened type.
//
// COM-840 — an upstream timeout or SCAPI server fault degrades the same way,
// but STALE-marked: the client keeps its last good basket rather than painting
// an empty one, and retries on the next refresh. 200, never cached (the path
// is no-store). Contract: `~/hec/basket/basket-loader-soft.logic.ts`.
//
// COM-902 — that stale answer is for a failed BASKET read only. Once the basket
// has loaded, a stalled enrichment read (products, promotion callouts, the TPA
// purchase-limit check) is settled inside `loaderImpl` and answers the real
// basket marked partial; it never reaches this catch.
export const loader = async (args: LoaderArgs) => {
  const startedAt = Date.now();
  try {
    return await loaderImpl(args);
  } catch (error) {
    if (error instanceof SlasMintThrottledError) {
      console.warn(
        "[resources/basket] SLAS mint throttled — degrading to an empty minicart",
      );
      return degradedBasketPayload(args.request);
    }
    const softReason = classifyBasketLoaderFailure(error);
    if (softReason) {
      // COM-886: name the budget that ran out (`scapi:getBasket`, `slas`, ...)
      // — a bare TimeoutError could be any upstream the loader awaits.
      console.warn(
        `[resources/basket] ${softReason} — answering a stale basket payload ${basketLoaderFailureDetail(
          error,
          Date.now() - startedAt,
        )}`,
        error instanceof Error ? `${error.name}: ${error.message}` : error,
      );
      return {
        ...degradedBasketPayload(args.request),
        ...basketLoaderStaleMarker(softReason),
      };
    }
    throw error;
  }
};

/** The loader's shape with no basket — every field as the happy path returns
 *  it for an absent basket, so no consumer sees a widened type. */
function degradedBasketPayload(request: Request) {
  return {
    basket: null,
    products: [],
    promotionCallouts: {},
    purchaseLimitInfo: { isPurchaseLimitReached: false } as const,
    emptyMinicartContent: null,
    afid: getAfidFromRequest(request),
    needsPassword: false,
    needsEmail: false,
    // No basket to check the kick against, so no banner. SFRA still enforces
    // at checkout (ET-343).
    telehealthBlock: null,
    gtmViewCart: null,
    // No basket at all, so nothing is "partial" — the stale marker (or the
    // SLAS-throttle empty state) is what describes this payload.
    ...basketLoaderPartialMarker([]),
  };
}

const loaderImpl = async ({ request, context }: LoaderArgs) => {
  const { shop, preset } = context;

  // "resolve": adopt an existing basket (incl. one created on the SFRA side for
  // a shared guest/registered SLAS identity) without creating one. This is the
  // surface that feeds the header minicart count, so it must reflect the
  // shopper's real basket even when the Workers session has no basket pinned.
  const basket = await shop.getBasket("resolve", {
    throwOnUpstreamFailure: true,
  });
  const allProductIds = [
    ...new Set(
      (
        basket?.productItems?.flatMap(item => [
          item.productId,
          ...(item.bundledProductItems?.map(child => child.productId) ?? []),
        ]) ?? []
      ).filter((id): id is string => Boolean(id)),
    ),
  ];

  const emptyMinicartContent$ = basket?.productItems?.length
    ? Promise.resolve(null)
    : fetchOneEntry({
        model: ModelMap.popoverContent,
        apiKey: getBuilderConfig(preset).api_key,
        options: { query: { name: "Empty Minicart" } },
        userAttributes: { useScheduling: true },
        fetchOptions: { signal: AbortSignal.timeout(2500) },
      }).catch(err => {
        console.warn(
          "[basket] Empty Minicart Builder fetch failed:",
          err instanceof Error ? err.message : err,
        );
        return null;
      });

  // SFCC-11931: merchant-authored callout messages for the promotions behind
  // the basket's price adjustments (order- and item-level). `itemText` on the
  // adjustments is NOT the callout (it stays the internal promotion ID), so
  // the callouts come from ShopperPromotions, keyed by promotionId.
  const promotionIds = [
    ...(basket?.orderPriceAdjustments ?? []),
    ...(basket?.productItems?.flatMap(item => item.priceAdjustments ?? []) ??
      []),
  ].map(adjustment => adjustment.promotionId);

  // COM-902 — the basket has loaded; from here on an upstream stall costs
  // detail, never the basket. Each enrichment read degrades to its empty
  // fallback and marks the payload partial (`settleBasketEnrichment`); only a
  // failed basket READ above still answers the COM-840 stale payload.
  const enrichmentFailures: BasketEnrichmentFailure[] = [];
  const [products, purchaseLimitInfo, emptyMinicartContent, promotionCallouts] =
    await Promise.all([
      settleBasketEnrichment(
        "products",
        shop.getProducts(allProductIds).then(data => data.products ?? []),
        [] as C_ProductInfo[],
        enrichmentFailures,
      ),
      settleBasketEnrichment(
        "purchaseLimitInfo",
        getTpaPurchaseLimitInfo(request, basket),
        { isPurchaseLimitReached: false } as Awaited<
          ReturnType<typeof getTpaPurchaseLimitInfo>
        >,
        enrichmentFailures,
      ),
      emptyMinicartContent$,
      settleBasketEnrichment(
        "promotionCallouts",
        shop.getPromotionCallouts(promotionIds),
        {} as Record<string, string>,
        enrichmentFailures,
      ),
    ]);
  const productsDegraded = enrichmentFailures.some(f => f.part === "products");

  const afid = getAfidFromRequest(request);
  const needsPassword = await externalProfileNeedsPassword();
  // SFCC-11430 — emailless TPA-SSO users (e.g. Alegeus/WEX accounts created
  // without an email) must collect an email alongside the password at checkout.
  // `needsEmail` is a strict sub-condition of `needsPassword` (itself already
  // TPA-scoped: externally authenticated + no SFCC password), so guests and
  // normal customers can never reach it. The `getCurrentUser` read is gated
  // behind `needsPassword` to keep it off the minicart hot path, and fails safe
  // to `false` (SFRA re-enforces email collection at checkout regardless).
  let needsEmail = false;
  if (needsPassword) {
    try {
      const currentUser = await shop.getCurrentUser();
      needsEmail = !currentUser?.email;
    } catch {
      needsEmail = false;
    }
  }
  // GA4 `view_cart` payload (SFCC-11889 C5) — assembled here because this
  // loader already enriches every line's product detail; <ViewCartTracker>
  // (cart route only) pushes it once per cart route-view. Analytics-only and
  // best-effort: a failure degrades to "no event", never a broken basket
  // response. Empty basket ⇒ null (SFRA's `!empty(allProductLineItems)` guard)
  // and zero extra fetches — the category lookups are SWR-cached and share the
  // impressions endpoint's cache namespace.
  // COM-902: withheld on a partial — without product detail every line would
  // report a blank name / category / price, and `ViewCartTracker` fires once
  // per route-view, so a degraded event would be the one GA4 keeps. A later
  // complete load fires it instead.
  let gtmViewCart: ViewCartEvent | null = null;
  if (basket?.productItems?.length && !productsDegraded) {
    try {
      const siteName = siteNameFromSiteId(preset.siteId) ?? preset.siteId;
      // Variant lines carry no `primaryCategoryId` of their own (it lives on the
      // master), so backfill before resolving paths — otherwise every variant
      // line in the cart reports `item_category: ""` on `view_cart`, the same
      // defect as the add/remove events. One extra `getProducts`, and only when
      // the basket actually holds an uncategorized variant. SFCC-12189.
      const lines = await backfillVariantCategoryIds(
        shop,
        resolveCartLineDetail(basket.productItems as ProductItem[], products, {
          siteName,
          imagesHost: preset.imagesHost,
        }),
      );
      const categoryPaths = await resolveCategoryPaths(
        shop,
        lines.map(line => line.category),
      );
      gtmViewCart = buildViewCartEvent({
        lines,
        categoryPaths,
        currency: basket.currency ?? "USD",
        value:
          basket.orderTotal ??
          basket.productTotal ??
          lines.reduce((sum, line) => sum + line.linePrice, 0),
      });
    } catch {
      gtmViewCart = null;
    }
  }

  // ET-343 — the telehealth checkout-block banner. SFRA decided this at the crossing
  // and named the reason in a response header, which the proxy parked in the session;
  // all that's left is to check the blocking plan is still in the cart and pick the
  // copy. Rides this payload rather than the cart loader so the verdict and the lines
  // it depends on arrive together — resolving it earlier would paint a banner the
  // basket could then retract.
  //
  // ET-538 — with no (applicable) kick yet, a signed-in shopper's active plan is looked up
  // here, so the block is there on the first cart load rather than after a refused checkout.
  //
  // COM-902 — on a products partial this resolves to `null` (no line classifies as
  // telehealth without its product). That `null` means "unknown", not "no block":
  // `BasketProvider` keeps the last verdict instead (`isPartialPart(raw, "products")`).
  const telehealthBlock = await resolveCartTelehealthBlock({
    storedKick: getTelehealthCheckoutKick() ?? null,
    productItems: basket?.productItems,
    products,
    // The treatment registry names the plan in the banner's copy. A thunk,
    // read only once the resolver has seen a telehealth line — this loader
    // feeds every page's minicart, and a core cart must not pay for it.
    treatments: () => loadTreatments(context),
  });

  return {
    basket,
    products,
    promotionCallouts,
    purchaseLimitInfo,
    emptyMinicartContent,
    afid,
    needsPassword,
    needsEmail,
    telehealthBlock,
    gtmViewCart,
    ...basketLoaderPartialMarker(enrichmentFailures),
  };
};

// SCAPI surfaces optimistic-concurrency failures as a `{type, title, detail}`
// body envelope (no `.status`); `.type` is the only reliable signal. The
// canonical URN is `.../errors/basket-version-conflict` but the tail varies,
// so substring-match on `conflict` / `basket-version` / `basket_version`.
function isBasketVersionConflict(err: unknown): boolean {
  if (!(err instanceof ScapiBodyError)) return false;
  const marker = err.type.toLowerCase();
  return (
    marker.includes("conflict") ||
    marker.includes("basket-version") ||
    marker.includes("basket_version")
  );
}

// COM-63 — contain the keyed GET loader: BasketProvider is layout-mounted, and
// every post-mutation `fetcher.load()` of this route (after the fail-soft
// preflight) can still reject in the window between preflight and load. An
// uncontained rejection blacks out whatever page the shopper is on via the
// root boundary. `null` on failure → the provider's `??` falls through to the
// last-good `softBasket`, so the minicart keeps its last-known count. NOT
// route-gated — unlike SFL, the basket is needed everywhere.
//
// COM-840 — a STALE payload (the loader's SCAPI timeout / 5xx answer) resolves
// to the same `null`, so the provider keeps its last good basket and the
// invalidation refresh retries. No beacon: the server already warned.
const containedBasketLoader = containedClientLoader<
  Awaited<ReturnType<typeof loader>>
>("basket.clientLoader");
export const clientLoader = async (
  args: Parameters<typeof containedBasketLoader>[0],
) => keyedBasketResult(await containedBasketLoader(args));

// COM-63 (P1-1 C / P1-3) — client-side containment for a network-level
// submission failure (the server `action`'s try/catch never sees a rejected
// fetch). Two bugs collapse into one fix: an uncontained failure (a) bubbled
// to the root error boundary from the cart page, and (b) for a guest "Save
// for later" left the localStorage SFL write committed while the basket-remove
// leg failed → the item showed in BOTH lists (the remove-settle observer only
// rolls back on a validationError payload).
//
// Returns a validation-error shape (see `buildBasketClientActionFailure`): it
// satisfies `isValidationError` (so every settle observer takes its failure
// branch). For add/update/remove the errors are EMPTY so the flyout watcher
// falls back to its per-action copy ("Couldn't add to cart" /
// BASKET_REMOVE_FAILED / …) instead of a raw network-error string; the coupon
// intents carry their own message because the flyout skips them and the
// PromoCodeForm renders `globalErrors[0]` inline.
//
// COM-830: the same wrapper publishes every resolved submission to
// `~/hec/basket/basket-submission-events` — the completion signal the
// added-to-cart modal, the failure toasts, CartUpdateTracker and Quick View
// observe now that basket POSTs no longer revalidate the page loaders (see
// `utils/basket-client-action.ts`).
export const clientAction = basketClientAction;

export const action = async ({
  request,
  context: { shop, waitUntil, preset },
}: ActionArgs) => {
  const validation = await BasketSchemaValidator.validate(
    await request.clone().formData(),
  );

  /* Handle any validation errros here */
  if (validation.error)
    return validationError(validation.error, validation.submittedData);

  const data = validation.data;
  try {
    switch (data.action) {
      case "add": {
        // `data.productId` is the resolved variant SKU — it fully specifies the
        // variant, so only productId + quantity go to SCAPI. The selected
        // variation values (color/size/master-size/leg-length) ride along in the
        // form for analytics/repopulation, but SCAPI's ProductItem body rejects
        // unknown props (e.g. `color`) with "Bad Request" (422), which broke
        // variant add-to-cart (SFCC-11255). Do NOT forward them.
        const addedBasket = await shop.addItemToBasket({
          productId: data.productId,
          quantity: data.quantity,
        });
        const item = addedBasket.productItems?.find(
          p => p.productId === data.productId,
        );
        invariantResponseError(item, "Product item not found in cart");

        // COM-831 — ONE product read, after the add, shared by the modal's
        // productData, the addBack QRID check and the Cart Update event. It
        // covers `cartUpdateProductIds(basket, item)` — the basket's lines,
        // which include the added one — so `buildCartUpdateForAction` reuses
        // it instead of re-reading every basket product. The ET-287 PATCH
        // below only stamps a custom attribute, so the post-PATCH basket holds
        // the same product ids and this read still covers it.
        // COM-902: capped well under the 8 s SCAPI budget — the add has
        // committed, so a stall must not hold its response. Expiry rejects as
        // a timeout (see `capPostAddProductRead`).
        const addProducts$ = capPostAddProductRead(
          shop
            .getProducts(cartUpdateProductIds(addedBasket, item))
            .then(res => res.products ?? []),
        );
        const loadProducts = () => addProducts$;
        // The shared read must not fail the add (it has already committed): on
        // failure fall back to the single-product read the modal and the QRID
        // check used before COM-831. Analytics then degrades to no event —
        // `buildCartUpdateForAction` swallows the rejected `loadProducts`.
        let sharedProducts: C_ProductInfo[];
        try {
          sharedProducts = await addProducts$;
        } catch (e) {
          console.warn(
            "[resources/basket] shared product read failed; falling back to the added product only",
            e instanceof Error ? e.message : e,
          );
          // A timeout is not retried here (COM-804: no timeout retries; the
          // fallback would add up to another full deadline to a request whose
          // add already committed). The add still reports success — the modal
          // just has no productData and analytics degrades to no event. Any
          // other failure gets one single-id read, itself fail-soft.
          sharedProducts = isScapiTimeout(e)
            ? []
            : await shop
                .getProducts([data.productId])
                .then(res => res.products ?? [])
                .catch(() => [] as C_ProductInfo[]);
        }
        const productData = sharedProducts.find(
          p => p.id === data.productId,
        ) as C_ProductInfo;
        // ET-287: restore a removed telehealth line's intake reference (see
        // `shouldRestampAddBackQrid`). Best-effort like the first intake add —
        // a failed PATCH keeps the plain add's basket.
        let basket = addedBasket;
        if (
          item.itemId &&
          data.questionnaireResponseId &&
          shouldRestampAddBackQrid({
            source: data.source,
            questionnaireResponseId: data.questionnaireResponseId,
            product: productData,
            lineQuestionnaireResponseId: (item as ProductItem)
              .c_th_questionnaire_response_id,
          })
        ) {
          try {
            basket = await shop.updateItemInBasket(item.itemId, {
              productId: item.productId,
              quantity: item.quantity,
              c_th_questionnaire_response_id: data.questionnaireResponseId,
            } as ShopperBasketsTypes.ProductItem & CustomRequestBody);
          } catch (e) {
            console.warn(
              "[resources/basket] addBack QRID PATCH failed; line restored without its intake link",
              e instanceof Error ? e.message : e,
            );
          }
        }
        const productImage = productData
          ? (getVariantImage(productData) ?? [])
          : [];
        const addedItem = {
          name: item.productName,
          price: item.price,
          image: productImage,
          productData,
          quantity: data.quantity,
        };
        const addCartUpdate = await buildCartUpdateForAction({
          shop,
          basket,
          action: "add",
          actedOnItem: item,
          eventQuantity: data.quantity,
          loadProducts,
        });
        return {
          successfullyAdded: true,
          basket,
          addedItems: [addedItem],
          quantity: data.quantity,
          segmentCartUpdate: addCartUpdate ? [addCartUpdate] : [],
          // GTM add-context echoed for the CartUpdateTracker → GA4 add event
          // (items[0].source + GA4_quickviewAddToCart choice). Kept off the
          // Segment payload so the Segment "Cart Update" stays SFRA-shaped.
          gtmAddContext: {
            clickSource: data.clickSource,
            quickView: data.quickView === "true",
          },
        };
      }
      case "addMany": {
        const productIds = data.items.map(it => it.productId);
        const basket = await shop.addItemToBasket(
          data.items.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
          })),
        );
        // COM-831 — ONE product read for the whole addMany, after the add: the
        // post-add basket's lines (which `buildCartUpdateForAction` needs for
        // every per-line event) plus the requested ids (so an id the add didn't
        // land still gets its modal productData, as before). Every per-line
        // Cart Update event shares it through `loadProducts`, so N lines cost 1
        // read instead of 1 + N.
        const readIds = [
          ...new Set([...cartUpdateProductIds(basket), ...productIds]),
        ];
        // COM-902: same post-add cap as `add`.
        const manyProducts$ = readIds.length
          ? capPostAddProductRead(
              shop.getProducts(readIds).then(res => res.products ?? []),
            )
          : Promise.resolve([] as C_ProductInfo[]);
        const loadManyProducts = () => manyProducts$;
        // Same fail-soft as `add`: a failed shared read falls back to the
        // requested ids for the modal; per-line events degrade to none.
        let allProductData: C_ProductInfo[];
        try {
          allProductData = await manyProducts$;
        } catch (e) {
          console.warn(
            "[resources/basket] shared product read failed; falling back to the requested ids",
            e instanceof Error ? e.message : e,
          );
          // Same rule as `add`: no fallback read after a timeout; a non-timeout
          // failure gets one fail-soft read of the requested ids.
          allProductData = isScapiTimeout(e)
            ? []
            : await shop
                .getProducts(productIds)
                .then(res => res.products ?? [])
                .catch(() => [] as C_ProductInfo[]);
        }
        const productDataById = new Map(
          allProductData.map(p => [p.id, p] as const),
        );
        const addedItems = data.items.map(it => {
          const productData = productDataById.get(it.productId);
          const basketItem = basket.productItems?.find(
            p => p.productId === it.productId,
          );
          const productImage = productData
            ? (getVariantImage(productData) ?? [])
            : [];
          return {
            name: basketItem?.productName ?? productData?.name ?? "",
            price: basketItem?.price ?? 0,
            image: productImage,
            productData: productData as C_ProductInfo,
            quantity: it.quantity,
          };
        });
        // SFRA fires Cart Update per single add (not set/pidsObj adds); we
        // improve on that gap by emitting one event per added line (SFCC-10799).
        const manyCartUpdates = (
          await Promise.all(
            data.items.map(it => {
              const basketItem = basket.productItems?.find(
                p => p.productId === it.productId,
              );
              return basketItem
                ? buildCartUpdateForAction({
                    shop,
                    basket,
                    action: "add",
                    actedOnItem: basketItem,
                    eventQuantity: it.quantity,
                    loadProducts: loadManyProducts,
                  })
                : Promise.resolve(null);
            }),
          )
        ).filter((e): e is NonNullable<typeof e> => e !== null);
        // GA4 `select_item` (SFRA Cart.js:371) — the CYP cross-sell lines added
        // alongside the main product (items[0]). Built from the already-resolved
        // cart-update lines (master id / title / brand / variant / per-unit price
        // / category), so no extra SCAPI/category fetch. Matched by sku to stay
        // correct even if a buildCartUpdate line dropped to null.
        const cypSkus = new Set(data.items.slice(1).map(it => it.productId));
        // Stamp the CYP cross-sell lines with their source list so Segment can
        // distinguish CYP-driven adds (the select_item analog) on the per-line
        // Cart Update events; regular adds carry no list_id.
        const stampedCartUpdates = manyCartUpdates.map(e =>
          cypSkus.has(e.sku) ? { ...e, list_id: "cyp_pdp" } : e,
        );
        const selectItem = buildSelectItemEvent(
          stampedCartUpdates
            .filter(e => cypSkus.has(e.sku))
            .map(e => ({
              id: e.product_id,
              name: e.title,
              brand: e.brand || undefined,
              variant: e.variant,
              price: e.price,
              // Slash-joined so `buildGa4ListItems` re-splits into
              // item_category..4 — full path since SFCC-11889 C2.
              category: [
                e.category_level_1,
                e.category_level_2,
                e.category_level_3,
                e.category_level_4,
              ]
                .filter(Boolean)
                .join("/"),
            })),
        );
        return {
          successfullyAdded: true,
          basket,
          addedItems,
          quantity: data.items[0]?.quantity ?? 1,
          segmentCartUpdate: stampedCartUpdates,
          gtmAddContext: {
            clickSource: data.clickSource,
            quickView: data.quickView === "true",
          },
          ...(selectItem ? { gtmSelectItem: selectItem } : {}),
        };
      }
      case "remove": {
        // The removed line is gone from the post-removal basket, but the Cart
        // Update event needs its product id, name, qty and price. COM-831: the
        // client sends that from its basket snapshot (`removedLine`) instead of
        // the action pre-reading the whole basket on the critical path. Absent /
        // malformed (schema `.catch`) => no event, never a fallback read — the
        // same "degrade to no event" a failed pre-read had.
        const removedItem: ProductItem | undefined = data.removedLine
          ? { ...data.removedLine, itemId: data.itemId }
          : undefined;
        const basket = await shop.removeItemFromBasket(data.itemId);
        // One product read (the post-removal lines + the removed one) shared by the
        // Cart Update event and the ET-513 kick check, which needs the same set.
        let productsRead: Promise<C_ProductInfo[]> | undefined;
        const loadProducts = () => {
          const ids = cartUpdateProductIds(basket, removedItem);
          return (productsRead ??= ids.length
            ? shop.getProducts(ids).then(res => res.products ?? [])
            : Promise.resolve([]));
        };
        const [removeCartUpdate] = await Promise.all([
          removedItem
            ? buildCartUpdateForAction({
                shop,
                basket,
                action: "remove",
                actedOnItem: removedItem,
                eventQuantity: removedItem.quantity ?? 0,
                loadProducts,
              })
            : null,
          // ET-513 — removing the plan SFRA blocked is what its banner asks for, so
          // delete the stored verdict rather than just hiding it: otherwise re-adding a
          // plan for the same condition revives it and checkout stays shut. Never
          // throws, and bounded, so it can't fail or stall the removal.
          clearTelehealthKickIfPlanRemoved({
            cookieHeader: request.headers.get("Cookie"),
            productItems: basket.productItems,
            loadProducts,
            waitUntil,
          }),
        ]);
        return {
          successfullyRemoved: true,
          basket,
          segmentCartUpdate: removeCartUpdate ? [removeCartUpdate] : [],
        };
      }
      case "update": {
        // The Cart Update `quantity` is the delta (SFRA `modify` semantics:
        // updateQuantity − current quantity). COM-831: the client sends the
        // line's pre-edit quantity (`oldQuantity`) from its basket snapshot,
        // replacing a pre-update basket read on the critical path.
        //
        // `null` = "old quantity unknown" (absent, or rejected by the schema),
        // which is NOT the same as "the line was at qty 0". Collapsing both to
        // 0 mattered once SFCC-12175 W5 started mapping the delta's SIGN to
        // add_to_cart / remove_from_cart: on a DECREASE (3 → 1), `1 - 0 = +1`
        // would emit a phantom add_to_cart with revenue attached, in the wrong
        // direction. Unknown ⇒ no event — never a guessed delta, and never a
        // fallback basket read.
        const oldQty = data.oldQuantity ?? null;
        const basket = await shop.updateItemInBasket(data.itemId, {
          quantity: data.quantity,
        });
        const updatedItem = basket.productItems?.find(
          i => i.itemId === data.itemId,
        );
        // `oldQty === null` (old quantity unknown) suppresses the event for
        // BOTH sinks. Passing `eventQuantity: 0` would have suppressed only the
        // GA4 half — `buildCartUpdateEvent` forwards it verbatim, so Segment
        // would report a `modify` with `quantity: 0`, which reads as a removal to
        // Bloomreach-style consumers. The comment above says "degrade to no
        // event"; this makes that true.
        const updateCartUpdate =
          updatedItem && oldQty !== null
            ? await buildCartUpdateForAction({
                shop,
                basket,
                action: "modify",
                actedOnItem: updatedItem,
                eventQuantity: data.quantity - oldQty,
              })
            : null;
        return {
          successfullyEdited: true,
          basket,
          segmentCartUpdate: updateCartUpdate ? [updateCartUpdate] : [],
        };
      }
      case "addCoupon": {
        try {
          // SCAPI flattens "unknown code" and "code already in basket"
          // into the same `invalid-coupon-code` error type — we cannot
          // tell them apart from the response. Pre-check the basket so
          // we can surface SFRA's "Coupon already in cart" copy for the
          // duplicate case instead of the generic "invalid or expired".
          const currentBasket = await shop.getBasket();
          const codeAlreadyApplied = currentBasket?.couponItems?.some(
            c => c.code?.toLowerCase() === data.code.toLowerCase(),
          );
          if (codeAlreadyApplied) {
            return validationError(
              { fieldErrors: { code: "Coupon already in cart" } },
              validation.submittedData,
            );
          }

          const result = await shop.addCouponToBasket(data.code);
          return { successfullyAddedCoupon: true, basket: result };
        } catch (couponError) {
          // COM-804: a timeout is not "invalid or expired" — let the outer
          // catch answer 503 (PromoCodeForm renders `globalErrors[0]`).
          if (isScapiTimeout(couponError)) throw couponError;
          const message = await mapAddCouponError(couponError);
          return validationError(
            { fieldErrors: { code: message } },
            validation.submittedData,
          );
        }
      }
      case "removeCoupon": {
        const basket = await shop.removeCouponFromBasket(data.couponItemId);
        return { successfullyRemovedCoupon: true, basket };
      }
      case "addReward": {
        // Single-reward invariant — SFRA `Cart-ApplyReward` removes any
        // existing ANNEXCLOUD_REWARD PI before creating a new one so the
        // basket never carries two rewards simultaneously. Mirror that here.
        //
        // ⚠️ Non-atomic remove-then-add: SFRA wraps both calls in one
        // `Transaction.wrap` (`Cart.js:31-40`); SCAPI doesn't expose that
        // primitive so we issue two sequential requests. If the add fails
        // after the remove succeeds the member temporarily has no reward
        // applied — `PerksBanner`'s `disabled={isSubmitting}` guard
        // prevents double-submit, but on a hard add-failure we surface the
        // error to the UI via the fetcher's response so the caller can
        // toast and the member knows their previous reward was dropped.
        //
        // SFCC-12288: the reward is priced and checked BEFORE the basket is
        // touched, like SFRA `Cart-ApplyReward`: the client only names it, the
        // amount comes from the profile's catalog, and the cost is checked
        // against Annex's live balance. A refused apply keeps the current
        // reward in place.
        const decision = await resolveRewardToApply({
          rewardId: data.rewardId,
          siteId: preset.siteId,
        });
        if (!decision.ok) {
          return {
            successfullyAddedReward: false,
            rewardRejected: rewardRejectedCopy(decision.reason),
          } as const;
        }
        const { reward } = decision;
        if (
          data.amount !== undefined &&
          data.amount !== reward.rewardCurrency
        ) {
          console.warn(
            `[perks-reward] client amount ignored: client=${data.amount} saved=${reward.rewardCurrency} siteId=${preset.siteId}`,
          );
        }
        const currentBasket = await shop.getBasket();
        const existing = currentBasket?.paymentInstruments?.find(
          pi => pi.paymentMethodId === ANNEXCLOUD_REWARD_PAYMENT_ID,
        );
        if (existing?.paymentInstrumentId) {
          await shop.removePaymentInstrumentFromBasket(
            existing.paymentInstrumentId,
          );
        }
        try {
          const basket = await shop.addPaymentInstrumentToBasket({
            paymentMethodId: ANNEXCLOUD_REWARD_PAYMENT_ID,
            amount: reward.rewardCurrency,
            c_acRewardID: reward.rewardId,
            // SFRA `Cart-ApplyReward` sets these too, and the reward
            // processor's `Reverse` credits `acRewardPoints` back when the
            // order fails after the debit: without it the points are lost.
            c_acRewardPoints: String(reward.creditRequired),
            c_acName:
              reward.displayText || `$${Math.round(reward.rewardCurrency)} OFF`,
          });
          return { successfullyAddedReward: true, basket };
        } catch (rewardAddError) {
          console.error(
            "[SFCC-10897] addPaymentInstrumentToBasket failed after reward-PI remove",
            rewardAddError,
          );
          return {
            successfullyAddedReward: false,
            // Caller (`PerksBanner`) is expected to surface a toast and the
            // basket revalidation will reflect the post-remove state.
          };
        }
      }
      case "removeReward": {
        const basket = await shop.removePaymentInstrumentFromBasket(
          data.paymentInstrumentId,
        );
        return { successfullyRemovedReward: true, basket };
      }
      case "swap-variant": {
        // Locate the source line item to know the current quantity and detect
        // whether the target variant is already in the cart (SFRA's merge case
        // — `Cart-EditProductLineItem` adds qty + removes the source PLI).
        const currentBasket = await shop.getBasket();
        const sourceItem = currentBasket?.productItems?.find(
          p => p.itemId === data.itemId,
        );
        if (!sourceItem) {
          return validationError(
            "Cart item no longer exists",
            validation.submittedData,
          );
        }
        const sourceQty = sourceItem.quantity ?? 1;
        // If a quantity wasn't supplied, preserve the source PLI's quantity.
        const requestedQty = data.quantity ?? sourceQty;
        if (!Number.isFinite(requestedQty) || requestedQty < 1) {
          return validationError(
            "Quantity must be at least 1",
            validation.submittedData,
          );
        }

        // Same-variant + same-qty no-op: avoid a wasted PATCH and product
        // fetch when the user opens the dialog and clicks Update without
        // changing anything.
        if (
          data.productId === sourceItem.productId &&
          requestedQty === sourceQty
        ) {
          return { successfullySwapped: true, basket: currentBasket };
        }

        // SFRA: `requestLineItem.product.custom.maxOrderQuantity` blocks the
        // swap if the new variant has a stricter per-product cap. Mirror that.
        // Also fetch the source product to guard against a swap to a variant
        // of a *different* master — the dialog scopes the picker to one
        // master, but the action is the security boundary.
        if (!sourceItem.productId) {
          return validationError(
            "Cart item is missing product reference",
            validation.submittedData,
          );
        }
        // Single SCAPI round-trip for both products (max 24 ids per call;
        // 2 fits trivially). The shared-master check defends against a
        // crafted POST swapping to a variant of an unrelated product.
        const { products: pair } = await shop.getProducts([
          data.productId,
          sourceItem.productId,
        ]);
        const newProduct = pair.find(p => p.id === data.productId);
        const sourceProduct = pair.find(p => p.id === sourceItem.productId);
        if (!newProduct || !sourceProduct) {
          return validationError("Product not found", validation.submittedData);
        }

        // Validate the target is a sibling of the source via the variants
        // array on either side. SCAPI doesn't reliably populate
        // `c_parentMasterProductID` (some variants come back without it), so
        // comparing master IDs incorrectly rejects legitimate swaps.
        // `product.variants` is authoritative and what the dialog's picker
        // builds from, so anything reachable in the picker is a sibling.
        const sourceVariantIds = new Set(
          sourceProduct.variants?.map(v => v.productId) ?? [],
        );
        const newVariantIds = new Set(
          newProduct.variants?.map(v => v.productId) ?? [],
        );
        const sharesSibling =
          sourceProduct.id === newProduct.id ||
          sourceVariantIds.has(newProduct.id) ||
          newVariantIds.has(sourceProduct.id);
        if (!sharesSibling) {
          return validationError(
            "Selected variant does not belong to the same product",
            validation.submittedData,
          );
        }

        const maxOrderQuantity =
          (newProduct as { c_maxOrderQuantity?: number }).c_maxOrderQuantity ??
          null;

        // Detect merge case — another PLI already holds the target variant.
        const existingTarget = currentBasket?.productItems?.find(
          p => p.itemId !== data.itemId && p.productId === data.productId,
        );
        const mergedQty = existingTarget
          ? (existingTarget.quantity ?? 0) + requestedQty
          : requestedQty;
        if (maxOrderQuantity !== null && mergedQty > maxOrderQuantity) {
          return validationError(
            `This product has a per-order limit of ${maxOrderQuantity}.`,
            validation.submittedData,
          );
        }

        // Both merge and non-merge paths can race a concurrent basket mutation
        // and hit a basket-version conflict; wrap both in the same retryable
        // catch so users see "Cart changed" rather than a generic SCAPI error.
        try {
          if (existingTarget && existingTarget.itemId) {
            // Merge: bump existing PLI's qty then delete the source. The two
            // SCAPI mutations aren't atomic — if the second throws on a
            // non-conflict error (network/5xx), the target PLI is left with
            // the bumped quantity AND the source PLI still exists, double-
            // counting the user's stock. Compensate by reverting the target
            // qty before re-throwing. Rollback is best-effort (if it also
            // fails, surface the original error to the user).
            const originalTargetQty = existingTarget.quantity ?? 0;
            await shop.updateItemInBasket(existingTarget.itemId, {
              quantity: mergedQty,
            });
            try {
              const basket = await shop.removeItemFromBasket(data.itemId);
              return {
                successfullySwapped: true,
                basket,
                mergedItemId: existingTarget.itemId,
                removedItemId: data.itemId,
              };
            } catch (removeErr) {
              try {
                await shop.updateItemInBasket(existingTarget.itemId, {
                  quantity: originalTargetQty,
                });
              } catch (rollbackErr) {
                console.error(
                  "[basket:swap-variant] merge rollback failed",
                  rollbackErr,
                );
              }
              throw removeErr;
            }
          }

          const basket = await shop.updateItemInBasket(data.itemId, {
            productId: data.productId,
            quantity: requestedQty,
          });
          return { successfullySwapped: true, basket };
        } catch (swapErr) {
          if (isBasketVersionConflict(swapErr)) {
            return validationError(
              "Cart changed during update — please reopen and try again",
              validation.submittedData,
            );
          }
          throw swapErr;
        }
      }
    }
  } catch (e) {
    // `invariantResponseError` throws a pre-built 422 payload
    // (DataWithResponseInit / Response). RR7 routes thrown values to the
    // ErrorBoundary (full-page break); `return` puts them in `fetcher.data`.
    // Re-emit as a returned value so the descriptive message reaches the
    // client without breaking the page.
    if (
      e instanceof Response ||
      (e !== null &&
        typeof e === "object" &&
        (e as { type?: unknown }).type === "DataWithResponseInit")
    ) {
      return e as Response;
    }
    console.error(e);
    // COM-804: an upstream timeout is an outage with an UNKNOWN outcome, not
    // a validation failure — 503 + `upstreamTimeout` flag, shopper-safe copy.
    if (isScapiTimeout(e)) {
      return upstreamTimeoutError(validation.submittedData);
    }
    const message = await extractScapiErrorMessage(e);
    return validationError(message, validation.submittedData);
  }
};

// SFCC-10797: maps SCAPI addCouponToBasket errors to SFRA cart copy.
// Mirrors the errorCode table in base SFRA `Cart.js` `AddCoupon`, using
// org_hec's `error.unable.to.add.coupon` override for invalid/expired cases.

function messageForCouponErrorType(type: string): string {
  const marker = type.toLowerCase();
  if (marker.includes("coupon-code-already-in-basket")) {
    return "Coupon already in cart";
  }
  if (marker.includes("coupon-already-in-basket")) {
    return "Coupon cannot be combined with other coupons already in your cart.";
  }
  if (marker.includes("coupon-code-already-redeemed")) {
    return "Coupon already redeemed";
  }
  return "The code you entered is either invalid or expired";
}

async function mapAddCouponError(err: unknown): Promise<string> {
  const fallback = "The code you entered is either invalid or expired";
  if (err instanceof ScapiBodyError) {
    return messageForCouponErrorType(err.type);
  }
  try {
    const response = (err as { response?: Response })?.response;
    if (response && typeof response.json === "function") {
      const body = (await response
        .clone()
        .json()
        .catch(() => null)) as {
        type?: string;
        title?: string;
      } | null;
      if (body?.type) return messageForCouponErrorType(body.type);
      if (body?.title) return messageForCouponErrorType(body.title);
    }
  } catch {
    // fall through to fallback
  }
  return fallback;
}

// Cart subtotal (no shipping — see `getMinicartSubtotal`) next to the basket
// icon at lg+. Must match the flyout's total. min-w-15 matches the skeleton's
// w-15 (widest 3-digit total, "$000.00", is 58.4px) so the account trigger
// beside it never shifts.
const BasketTotalLabel = ({ subtotal }: { subtotal: number }) => {
  const formatPrice = useFormatPrice();
  return (
    <span className="min-w-15 hidden text-left font-grotesque text-[0.875rem] font-medium leading-none text-accent lg:ml-2 lg:mt-2 lg:inline-block">
      {formatPrice(subtotal)}
    </span>
  );
};

// COM-189 wave 4B: the minicart's overlay half — Radix Popover root/content +
// the flyout body (line items, empty-state ChromeContent, footer CTAs) — lives
// in ./components/minicart-popover.tsx and loads lazily. It was this module's
// only path to the popover/floating-ui graph and the line-item UI. The trigger
// and the `useHoverPopover` state stay HERE, so a hover/click that lands
// before the chunk buffers as `open=true` and the flyout appears on arrival.
const MinicartPopover = lazy(() => import("./components/minicart-popover"));

export const Minicart = () => {
  // COM-216: `emptyMinicartContent` comes from the CONTEXT, not the keyed
  // fetcher — the cold guest load is hydrated by the provider's fail-soft fetch
  // (SFCC-11929), which never populates the fetcher, so reading it there left
  // the empty-cart flyout blank until a basket mutation happened.
  // COM-840: the checkout-gate fields come from the CONTEXT too (the
  // provider's last-good selection), not the raw keyed fetcher: a stalled
  // refresh resolves that fetcher to `null` and would reset them to "allowed".
  const {
    basket,
    emptyMinicartContent,
    purchaseLimitInfo,
    needsPassword,
    needsEmail,
    afid,
  } = useBasket() ?? {};
  const isPurchaseLimitReached =
    purchaseLimitInfo?.isPurchaseLimitReached === true;
  const url = useURL();
  const {
    open,
    setOpen,
    handleOpen,
    handleClose,
    triggerRef,
    onCloseAutoFocus,
  } = useHoverPopover<HTMLAnchorElement>();
  // COM-189 wave 4B: gates the lazy MinicartPopover mount below.
  const overlaysReady = useOverlaysReady();

  // SFCC-11845: routes that render a full cart view hide the redundant header
  // mini-cart entirely via `handle.hideMinicart` (see cart.tsx) — matching SFRA,
  // which omits the whole minicart on `Cart-Show`.
  const hideMinicart = isMinicartHidden(useMatches());

  // `standalone` renders the trigger OUTSIDE a Radix PopoverTrigger (SSR and
  // the pre-overlays-ready client render): the static dialog attributes mirror
  // what PopoverTrigger injects, so the server document reads the same as it
  // did when the Popover was in the synchronous graph.
  const renderTrigger = (standalone: boolean) => (
    // `/cart` is a Workers/RR7 route (no longer SFRA-owned), so the hybrid
    // `<Link>` keeps this as a client-side SPA navigation instead of a full
    // document reload. `Button asChild` preserves the `linkInline` styling.
    <Button
      asChild
      variant="linkInline"
      className="relative inline-flex h-[45px] min-w-[2.3125rem] items-center hover:no-underline lg:mr-5 sm-max:text-lg"
    >
      <Link
        ref={triggerRef}
        to={url("/cart")}
        aria-label="Open cart"
        data-testid="minicart-trigger"
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        {...(standalone
          ? {
              "aria-haspopup": "dialog" as const,
              "aria-expanded": false,
              "data-state": "closed",
            }
          : {})}
      >
        <span className="relative inline-flex items-center text-accent">
          <MinicartIcon />
          <SkeletonAwait
            resolve={basket}
            forceSkeleton={!basket}
            className="absolute -right-1 -top-1 h-[1.125rem] w-[1.125rem] -translate-y-2/4 translate-x-2/4 rounded-full"
          >
            {basket => {
              const productQty =
                basket?.productItems?.reduce?.(
                  (acc, currentValue) => acc + (currentValue?.quantity || 0),
                  0,
                ) ?? 0;
              return (
                <span className="absolute -right-1 -top-1 block h-[1.125rem] w-[1.125rem] -translate-y-2/4 translate-x-2/4 rounded-full bg-accent text-center text-[0.6875rem] font-medium leading-[1.125rem] text-accent-foreground">
                  {productQty}
                </span>
              );
            }}
          </SkeletonAwait>
        </span>
        <SkeletonAwait
          resolve={basket}
          forceSkeleton={!basket}
          className="w-15 hidden h-[0.875rem] lg:ml-2 lg:mt-2 lg:inline-block"
        >
          {basket => (
            <BasketTotalLabel
              subtotal={basket ? getMinicartSubtotal(basket) : 0}
            />
          )}
        </SkeletonAwait>
      </Link>
    </Button>
  );

  // Hide the whole control (icon + count) on routes that opt out, e.g. the cart
  // page. Kept separate from the `!basket` skeleton-loading case below.
  if (hideMinicart) {
    return null;
  }

  if (!basket) {
    return renderTrigger(false);
  }

  // Standalone trigger until the lazy popover may mount (SSR always takes this
  // branch — overlaysReady is false on the server). An `open` flip forces the
  // mount so a pre-idle hover/click shows the flyout the moment the chunk
  // lands; the Suspense fallback keeps the trigger on screen while it loads.
  if (!overlaysReady && !open) {
    return renderTrigger(true);
  }

  return (
    // OverlayBoundary (PR #1042 blocker 3): a rejected minicart chunk
    // degrades to the plain trigger instead of unmounting the page.
    <OverlayBoundary overlay="minicart-popover" fallback={renderTrigger(true)}>
      <Suspense fallback={renderTrigger(true)}>
        <MinicartPopover
          open={open}
          onOpenChange={setOpen}
          onHoverOpen={handleOpen}
          onHoverClose={handleClose}
          onCloseAutoFocus={onCloseAutoFocus}
          trigger={renderTrigger(false)}
          basket={basket}
          isPurchaseLimitReached={isPurchaseLimitReached}
          needsPassword={needsPassword ?? false}
          needsEmail={needsEmail ?? false}
          afid={afid ?? null}
          emptyMinicartContent={emptyMinicartContent ?? null}
        />
      </Suspense>
    </OverlayBoundary>
  );
};
